import { User, Edit, KeyRound, LogOut, Pointer } from 'lucide-react';
import user_icon from "../assets/svg/user_icon.svg"
import search_icons from "../assets/svg/search_icons.svg"
import remove_icon from "../assets/svg/removed_icon.svg"
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/PermissionContext';
import "../styles/CommonNavbar.css"
import { X } from 'lucide-react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';


const CommonNavbar = ({ onSearch, disableSearch = false }) => {
  const { setPermissions } = usePermissions();

  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const profileRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileData, setProfileData] = useState({
    name: 'User',
    email: 'user@example.com',
    image: user_icon
  });

  const toggleProfileCard = () => {
    setShowProfileCard(!showProfileCard);
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await axiosInstance.get('/basicprofile');
        const user = response.data.user;
        setProfileData({
          name: user.first_name || 'User',
          email: user.email || 'user@example.com',
          image: user.profile_image || user_icon
        });
      } catch (err) {

        console.error('Error fetching profile data:', err);
      }
    };
    if (showProfileCard) {
      fetchProfileData();
    }
  }, [showProfileCard]);


  // Basic Profile Details
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileCard(false);
      }
    };

    if (showProfileCard) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileCard]);


  // Logout
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await axiosInstance.get('/logout');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem("permissions");
      setPermissions([]);
      toast.success("Logged out successfully");
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.message || "Logout failed");
    }
  };

  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = async () => {
    if (disableSearch) {
      toast.warn("Search cannot be performed here.");
      return;
    }
    if (!searchTerm.trim()) {
      toast.warn("Please enter a search term.");
      return;
    }

    try {
      await onSearch(searchTerm);
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Something went wrong while searching.");
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === '' && onSearch) {
      onSearch('');
    }
  };

  const [expanded, setExpanded] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const fullText = 'Search here...';

  useEffect(() => {
    const timer = setTimeout(() => {
      setExpanded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (expanded) {
      let index = 0;
      const typing = setInterval(() => {
        setPlaceholderText(fullText.slice(0, index));
        index++;
        if (index > fullText.length) clearInterval(typing);
      }, 150); 
      return () => clearInterval(typing);
    } else {
      setPlaceholderText('');
    }
  }, [expanded]);

  const handleSavePassword = async () => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = "Please enter your current password.";
    if (!newPassword) newErrors.newPassword = "Please enter a new password.";
    if (!confirmPassword) newErrors.confirmPassword = "Please confirm your password.";
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/;
    if (newPassword && !passwordRegex.test(newPassword)) {
      newErrors.newPassword =
        "Password must include 1 capital, 1 number, 1 symbol (min 8 chars).";
    }
    if (newPassword && confirmPassword && newPassword !== confirmPassword)
      newErrors.confirmPassword = "New Password and Confirm Password do not match.";

    if (Object.keys(newErrors).length > 0) {
      setErrorMessage(newErrors);
      return;
    }
    setErrorMessage({ currentPassword: "", newPassword: "", confirmPassword: "" });

    try {
      const response = await axiosInstance.post("/user/update_password", {
        password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data.success) {
        toast.success("Password updated successfully!");
        setShowChangePassword(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        setTimeout(() => {
          navigate("/login");
        }, 1000);
      } else {
        setErrorMessage(response.data.message || "Failed to update password.");
      }
    } catch (error) {
      const backendMessage = error.response?.data?.message;
      if (backendMessage === "Current Password is Wrong") {
        setErrorMessage({ currentPassword: backendMessage });
      } else {
        toast.error("❌ Something went wrong while updating the password.");
      }
    }
  };


  const getPasswordStrength = (password) => {
    if (!password) return { label: "", color: "#e0e0e0", strength: 0 };

    let hasUpper = /[A-Z]/.test(password);
    let hasLower = /[a-z]/.test(password);
    let hasNumber = /[0-9]/.test(password);
    let hasSymbol = /[^A-Za-z0-9]/.test(password);
    let isLong = password.length >= 8;

    let strength = 0;
    if (hasLower || hasUpper) strength++;
    if (hasUpper && hasLower) strength++;
    if (hasNumber) strength++;
    if (hasSymbol) strength++;
    if (isLong) strength++;

    if (strength <= 2) {
      return { label: "Weak Password", color: "#e63946", strength: 1 };
    } else if (strength === 3 || strength === 4) {
      return { label: "Medium Password", color: "#f7c948", strength: 3 };
    } else {
      return { label: "Strong Password", color: "#2ecc71", strength: 4 };
    }
  };

  const resetPasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrorMessage({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };



  return (
    <div className="nav-items">
      <div
       className="search-bar"
      style={{
        width: expanded ? '280px' : '40px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: expanded ? 'space-between' : 'center',
        background: '#F1F3F4',
        borderRadius: '37px',
        padding: '5px 10px',
        height: '40px',
        gap: '5px',
        transition: 'width 0.6s ease, justify-content 0.6s ease',
        overflow: 'hidden',
      }}
    >
      {/* Search Icon */}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          transition: 'all 0.3s ease',
            cursor: disableSearch ? 'not-allowed' : 'pointer',
          flexShrink: 0,
        }}
        onClick={() => handleSearch()}
      >
        <img
          src={search_icons}
          alt="search-icon"
          style={{
            width: '16px',
            height: '16px',
            opacity: 0.7,
          }}
        />
      </span>

      {/* Input field with animated placeholder */}
      {expanded && (
        <input
          type="text"
          className="nav-search-input"
          placeholder={placeholderText}
          value={searchTerm}
            onChange={disableSearch ? undefined : handleChange}
            onKeyDown={disableSearch ? undefined : handleKeyDown}
            readOnly={disableSearch}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '13px',
            fontWeight: '400',
            color: '#333',
            paddingLeft: '5px',
            margin: '0px',
            transition: 'all 0.3s ease',
              cursor: disableSearch ? 'not-allowed' : 'text',
          }}
        />
      )}

      {/* Clear button */}
      {expanded && searchTerm && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#fff',
            border: 'none',
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
              cursor: disableSearch ? 'not-allowed' : 'text',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            transition: 'all 0.2s ease',
            padding: 0,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#f0f0f0';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#fff';
          }}
        >
          <img src={remove_icon} alt="clear" style={{ width: '16px', height: '16px' }} />
        </button>
      )}
       
    </div>


      <div ref={profileRef}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'relative'
          }}
          onClick={toggleProfileCard}
        >
          <img
            src={user_icon}
            alt="avatar"
            className="rounded-full"
            width={33}
            height={33}
            style={{ cursor: 'pointer' }}
          />
        </div>

        {/* Profile Card */}
        {showProfileCard && (
          <div
            style={{
              position: 'absolute',
              right: '20px',
              top: '60px',
              width: '300px',
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
              fontFamily: 'Inter, system-ui, sans-serif',
              zIndex: 1000
            }}
            onClick={(e) => e.stopPropagation()} // Prevent click from closing the card
          >
            {/* Profile Image and Name Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '14px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '7px',
                overflow: 'hidden'
              }}>
                <img
                  src={user_icon}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <h2 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#1A1F2C'
              }}>
                {profileData.name}
              </h2>
              <span style={{
                margin: '4px 0 0',
                fontSize: '12px',
                color: '#8E9196'
              }}>
                {profileData.email}
              </span>
            </div>

            {/* Menu Items */}
            <div>
              {/* View Profile */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                cursor: 'pointer'
              }}
                onClick={() => {
                  setShowProfileCard(false);
                  navigate("/userProfile");
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '12px',
                  color: '#1A1F2C'
                }}>
                  <User size={18} strokeWidth={1.4} />
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#1A1F2C'
                }}>
                  View profile
                </span>
              </div>

              {/* Edit Profile */}
              {/* <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                cursor: 'pointer'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '12px',
                  color: '#1A1F2C'
                }}>
                  <Edit size={18} strokeWidth={1.5} />
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#1A1F2C'
                }}>
                  Edit profile
                </span>
              </div> */}

              {/* Change Password */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                cursor: 'pointer'
              }} onClick={() => {
                setShowProfileCard(false);
                setShowChangePassword(true);
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '12px',
                  color: '#1A1F2C'
                }}>
                  <KeyRound size={18} strokeWidth={1.5} />
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#1A1F2C',

                }}>
                  Change password
                </span>
              </div>

              {/* Sign Out */}
              <div onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 0',
                  cursor: 'pointer'
                }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '12px',
                  color: '#FF3B30'
                }}>
                  <LogOut size={18} strokeWidth={1.5} />
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#FF3B30'
                }}>
                  Sign out
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Card */}
        {showChangePassword && (
          <div className="change-password-overlay" onClick={() => setShowChangePassword(false)}
          >
            <div className="change-password-popup" onClick={(e) => e.stopPropagation()}>

              <div className="change-password-header">
                <h3 className="change-password-title">Change Password</h3>
                <div
                  className="change-password-close-btn"
                  onClick={() => {
                    setShowChangePassword(false);
                    resetPasswordFields();
                  }}
                >
                  <X size={16} />
                </div>
              </div>

              <span className="password-requirements">
                Password must contain at least 1 Captial letter, 1 number, and 1 symbol. Minimum length is 8 characters.
              </span>

              {/* ===== Current Password ===== */}
              <div className="change-password-field">
                <label>Current Password <span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setErrorMessage((prev) => ({ ...prev, currentPassword: "" }));
                    }}
                    style={{ margin: "0px" }}
                    autoComplete="new-password"
                    name="new-password"
                    placeholder="Enter current password"
                  />
                  <span
                    className="password-eye-icon"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </span>
                </div>
                {errorMessage.currentPassword && (
                  <div className="password-error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    {errorMessage.currentPassword}
                  </div>
                )}
              </div>

              {/* ===== New Password ===== */}
              <div className="change-password-field">
                <label>New Password <span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrorMessage((prev) => ({ ...prev, newPassword: "" }));
                    }}
                    style={{ margin: '0px' }}
                    placeholder="New password"
                  />
                  <span
                    className="password-eye-icon"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </span>
                </div>
                {errorMessage.newPassword && (
                  <div className="password-error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    {errorMessage.newPassword}
                  </div>
                )}
              </div>

              {/* ===== Confirm Password ===== */}
              <div className="change-password-field">
                <label>Confirm Password <span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrorMessage((prev) => ({ ...prev, confirmPassword: "" }));
                    }}
                    placeholder="Confirm new password"
                    style={{ margin: '0px' }}
                  />
                  <span
                    className="password-eye-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </span>
                </div>
                {errorMessage.confirmPassword && (
                  <div className="password-error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    {errorMessage.confirmPassword}
                  </div>
                )}
              </div>

              {newPassword && (
                <div className="password-strength-container">
                  <div
                    className="password-strength-bar"
                    style={{
                      width: `${(getPasswordStrength(newPassword).strength / 4) * 100}%`,
                      backgroundColor: getPasswordStrength(newPassword).color,
                    }}
                  ></div>
                  <span
                    className="password-strength-label"
                    style={{ color: getPasswordStrength(newPassword).color }}
                  >
                    {getPasswordStrength(newPassword).label}
                  </span>
                </div>
              )}

              {/* {errorMessage && (
                <div className="password-error-message">{errorMessage}</div>
              )} */}

              <div className="change-password-buttons">
                <button className="cancel-btn" onClick={() => {
                  setShowChangePassword(false);
                  resetPasswordFields();
                }}>
                  Cancel</button>
                <button className="save-btn" onClick={handleSavePassword}>Save</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default CommonNavbar
